import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const RegistrationsContext = createContext(null);

/**
 * Global registrations store.
 *
 * Provides:
 *   registrations  – flat array of all registration rows for this team
 *   regsByEvent    – { [eventId]: [reg, ...] }
 *   regsByDriver   – { [driverId]: [reg, ...] }
 *   refresh()      – re-fetches from API
 *   register(eventId, driverId, status)   – POST + refresh
 *   unregister(registrationId)            – DELETE + refresh
 *   setStatus(eventId, driverId, status)  – UPSERT + refresh (used by matrix)
 */
export function RegistrationsProvider({ token, children }) {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get('/api/registrations', { headers });
      setRegistrations(res.data || []);
    } catch (err) {
      console.error('[RegistrationsContext] refresh failed:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Initial fetch on mount / token change
  useEffect(() => { refresh(); }, [refresh]);

  const register = useCallback(async (eventId, driverId, status = 'participating') => {
    const res = await axios.post('/api/registrations', {
      event_id: eventId,
      driver_id: driverId,
      status,
    }, { headers });
    // optimistic local update
    setRegistrations(prev => {
      const filtered = prev.filter(r => !(r.event_id === eventId && r.driver_id === driverId));
      return [...filtered, { ...res.data, event_id: eventId, driver_id: driverId, status }];
    });
    return res.data;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const unregister = useCallback(async (registrationId) => {
    await axios.delete(`/api/registrations/${registrationId}`, { headers });
    setRegistrations(prev => prev.filter(r => r.id !== registrationId));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Used by RaceMatrix to cycle through statuses
  const setStatus = useCallback(async (eventId, driverId, status) => {
    if (status === null) {
      // find and delete
      const existing = registrations.find(r => r.event_id === eventId && r.driver_id === driverId);
      if (existing) {
        await axios.delete(`/api/registrations/${existing.id}`, { headers });
        setRegistrations(prev => prev.filter(r => r.id !== existing.id));
      }
    } else {
      const res = await axios.post('/api/registrations', {
        event_id: eventId,
        driver_id: driverId,
        status,
      }, { headers });
      setRegistrations(prev => {
        const filtered = prev.filter(r => !(r.event_id === eventId && r.driver_id === driverId));
        return [...filtered, { id: res.data.id, event_id: eventId, driver_id: driverId, status }];
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, registrations]);

  // Derived lookups
  const regsByEvent = registrations.reduce((acc, r) => {
    if (!acc[r.event_id]) acc[r.event_id] = [];
    acc[r.event_id].push(r);
    return acc;
  }, {});

  const regsByDriver = registrations.reduce((acc, r) => {
    if (!acc[r.driver_id]) acc[r.driver_id] = [];
    acc[r.driver_id].push(r);
    return acc;
  }, {});

  return (
    <RegistrationsContext.Provider value={{
      registrations,
      regsByEvent,
      regsByDriver,
      loading,
      refresh,
      register,
      unregister,
      setStatus,
    }}>
      {children}
    </RegistrationsContext.Provider>
  );
}

export function useRegistrations() {
  const ctx = useContext(RegistrationsContext);
  if (!ctx) throw new Error('useRegistrations must be used inside RegistrationsProvider');
  return ctx;
}
